import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, Loader2, Upload, FileCheck2, Shield, X } from "lucide-react";
import { API } from "../lib/api";

const MAX_SIZE_MB = 10;
const ACCEPT = "application/pdf,image/png,image/jpeg,image/webp,image/heic";

function FileField({ label, hint, required, value, onChange, testid }) {
  const inputRef = React.useRef(null);
  const click = () => inputRef.current?.click();
  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`${f.name} er over ${MAX_SIZE_MB} MB`);
      e.target.value = "";
      return;
    }
    onChange(f);
  };
  return (
    <div>
      <label className="label-flat flex items-center gap-2">
        {label} {required && <span className="text-brand-red">*</span>}
      </label>
      <div
        className={`mt-1 border-2 border-dashed p-4 cursor-pointer transition ${value ? "border-emerald-400 bg-emerald-50/40" : "border-slate-300 hover:border-brand-red bg-slate-50/40"}`}
        onClick={click}
        data-testid={testid}
      >
        <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={onPick} data-testid={`${testid}-input`} />
        {value ? (
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 text-emerald-800 font-semibold min-w-0">
              <FileCheck2 size={18} className="flex-shrink-0" />
              <span className="truncate">{value.name}</span>
              <span className="text-emerald-700/70 text-xs flex-shrink-0">({(value.size / 1024 / 1024).toFixed(2)} MB)</span>
            </div>
            <button
              type="button"
              className="text-slate-500 hover:text-red-700"
              onClick={(e) => { e.stopPropagation(); onChange(null); if (inputRef.current) inputRef.current.value = ""; }}
              data-testid={`${testid}-clear`}
              title="Fjern fil"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-3 text-sm text-brand-text2">
            <Upload size={18} className="text-brand-text2" />
            <div>
              <div className="font-semibold text-brand-dark">Klik for at uploade</div>
              <div className="text-xs">{hint || "PDF, JPG eller PNG · max 10 MB"}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DriverPage() {
  const [form, setForm] = React.useState({
    full_name: "", email: "", phone: "", city: "",
    address: "", zip_code: "", license_plate: "",
    has_drivers_license: true, has_own_vehicle: false,
    is_danish_citizen: true, nationality: "",
    experience: "", motivation: "",
  });
  const [files, setFiles] = React.useState({
    drivers_license_front: null,
    drivers_license_back: null,
    criminal_record_doc: null,
    work_permit_doc: null,
    passport_doc: null,
    cv_doc: null,
  });
  const [gdpr, setGdpr] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k) => setForm((f) => ({ ...f, [k]: !f[k] }));
  const setFile = (k) => (v) => setFiles((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.city) {
      toast.error("Udfyld navn, email, telefon og by"); return;
    }
    if (!form.address || !form.zip_code) {
      toast.error("Udfyld venligst adresse og postnummer"); return;
    }
    if (form.has_own_vehicle && !form.license_plate) {
      toast.error("Når du har egen bil, skal du angive nummerplade"); return;
    }
    if (!files.drivers_license_front) {
      toast.error("Vedhæft venligst kørekort (forside)"); return;
    }
    if (!files.drivers_license_back) {
      toast.error("Vedhæft venligst kørekort (bagside)"); return;
    }
    if (!files.criminal_record_doc) {
      toast.error("Vedhæft venligst straffeattest (PDF eller foto)"); return;
    }
    if (form.is_danish_citizen && !files.passport_doc) {
      toast.error("Som dansk statsborger skal du vedhæfte dit pas"); return;
    }
    if (!form.is_danish_citizen && !files.work_permit_doc) {
      toast.error("Hvis du ikke er dansk statsborger, skal du vedhæfte opholdstilladelse/arbejdstilladelse"); return;
    }
    if (!gdpr) {
      toast.error("Du skal acceptere GDPR-samtykke for at sende ansøgningen"); return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, typeof v === "boolean" ? (v ? "true" : "false") : (v || "")));
      fd.append("gdpr_consent", "true");
      if (files.drivers_license_front) fd.append("drivers_license_front", files.drivers_license_front);
      if (files.drivers_license_back) fd.append("drivers_license_back", files.drivers_license_back);
      if (files.criminal_record_doc) fd.append("criminal_record_doc", files.criminal_record_doc);
      if (files.work_permit_doc) fd.append("work_permit_doc", files.work_permit_doc);
      if (files.passport_doc) fd.append("passport_doc", files.passport_doc);
      if (files.cv_doc) fd.append("cv_doc", files.cv_doc);
      const res = await fetch(`${API}/drivers/apply-with-docs`, { method: "POST", body: fd });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Server-fejl");
      }
      setSent(true);
      toast.success("Tak! Vi vender tilbage hurtigst muligt.");
    } catch (err) {
      toast.error(err.message || "Noget gik galt — prøv igen.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="driver-page">
      <section className="bg-brand-dark text-white py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="kicker text-brand-red">Bliv chauffør</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 leading-[1.05] max-w-3xl">
            Kør med os. Bliv en del af et voksende dansk hold.
          </h1>
          <p className="text-white/70 mt-6 text-lg max-w-2xl leading-relaxed">
            Vi søger pålidelige chauffører på Sjælland til same-day, express og aftenlevering. Fleksibel arbejdstid, god betaling og et professionelt team.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-16">
        {sent ? (
          <div className="bg-white border border-brand p-10 text-center" data-testid="driver-sent">
            <div className="w-14 h-14 bg-brand-red text-white mx-auto flex items-center justify-center"><CheckCircle2 size={28} /></div>
            <h2 className="font-display font-bold text-3xl text-brand-dark mt-6">Tak for din ansøgning!</h2>
            <p className="text-brand-text2 mt-3">Vi gennemgår alle ansøgninger og vender tilbage hurtigst muligt på din email.</p>
            <p className="text-brand-text2 mt-2 text-sm">Dine dokumenter behandles fortroligt og slettes når ansøgningen er afsluttet.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-brand p-7 sm:p-10 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="driver-form" encType="multipart/form-data">
            <div className="sm:col-span-2">
              <div className="kicker">Ansøgningsformular</div>
              <div className="font-display font-semibold text-2xl text-brand-dark mt-2">Fortæl os om dig selv</div>
            </div>
            <div>
              <label className="label-flat">Fulde navn <span className="text-brand-red">*</span></label>
              <input className="input-flat" value={form.full_name} onChange={update("full_name")} data-testid="driver-name" required />
            </div>
            <div>
              <label className="label-flat">Email <span className="text-brand-red">*</span></label>
              <input type="email" className="input-flat" value={form.email} onChange={update("email")} data-testid="driver-email" required />
            </div>
            <div>
              <label className="label-flat">Telefon <span className="text-brand-red">*</span></label>
              <input className="input-flat" value={form.phone} onChange={update("phone")} data-testid="driver-phone" required />
            </div>
            <div>
              <label className="label-flat">By <span className="text-brand-red">*</span></label>
              <input className="input-flat" value={form.city} onChange={update("city")} data-testid="driver-city" required />
            </div>

            <div>
              <label className="label-flat">Adresse <span className="text-brand-red">*</span></label>
              <input className="input-flat" value={form.address} onChange={update("address")} data-testid="driver-address" placeholder="Fx Hovedgaden 12, 2. tv" required />
            </div>
            <div>
              <label className="label-flat">Postnummer <span className="text-brand-red">*</span></label>
              <input className="input-flat" value={form.zip_code} onChange={update("zip_code")} data-testid="driver-zip" placeholder="Fx 2300" inputMode="numeric" pattern="[0-9]{4}" maxLength={4} required />
            </div>

            <div className="sm:col-span-2 flex flex-wrap gap-4 mt-2">
              <label className="flex items-center gap-2 text-sm font-medium text-brand-dark cursor-pointer">
                <input type="checkbox" checked={form.has_drivers_license} onChange={() => toggle("has_drivers_license")} data-testid="driver-license" />
                Jeg har gyldigt kørekort
              </label>
              <label className="flex items-center gap-2 text-sm font-medium text-brand-dark cursor-pointer">
                <input type="checkbox" checked={form.has_own_vehicle} onChange={() => toggle("has_own_vehicle")} data-testid="driver-vehicle" />
                Jeg har egen bil
              </label>
              <label className="flex items-center gap-2 text-sm font-medium text-brand-dark cursor-pointer">
                <input type="checkbox" checked={form.is_danish_citizen} onChange={() => toggle("is_danish_citizen")} data-testid="driver-danish-citizen" />
                Jeg er dansk statsborger
              </label>
            </div>

            {form.has_own_vehicle && (
              <div className="sm:col-span-2" data-testid="driver-plate-block">
                <label className="label-flat">Bilens nummerplade <span className="text-brand-red">*</span></label>
                <input
                  className="input-flat uppercase tracking-wider font-mono"
                  value={form.license_plate}
                  onChange={(e) => setForm((f) => ({ ...f, license_plate: e.target.value.toUpperCase().replace(/\s+/g, "") }))}
                  data-testid="driver-license-plate"
                  placeholder="Fx AB12345"
                  maxLength={10}
                  required
                />
                <div className="text-xs text-brand-text2 mt-1">Bruges til erhvervsforsikring og kontrakt — kan kontrolleres på Motorregistret.</div>
              </div>
            )}

            {!form.is_danish_citizen && (
              <div className="sm:col-span-2 bg-amber-50 border-l-4 border-amber-500 p-4" data-testid="driver-nationality-block">
                <label className="label-flat">Statsborgerskab / nationalitet <span className="text-brand-red">*</span></label>
                <input className="input-flat" value={form.nationality} onChange={update("nationality")} data-testid="driver-nationality" placeholder="Fx tyrkisk, polsk, syrisk" required />
                <div className="text-xs text-amber-800 mt-2">Du skal vedhæfte gyldig arbejdstilladelse eller opholdstilladelse nedenfor.</div>
              </div>
            )}

            {/* Documents */}
            <div className="sm:col-span-2 border-t border-brand pt-6 mt-2">
              <div className="kicker">Dokumenter</div>
              <div className="font-display font-semibold text-xl text-brand-dark mt-2">Vedhæft dine papirer</div>
              <p className="text-sm text-brand-text2 mt-2">Vi skal bruge kørekort (forside + bagside), straffeattest og pas (for danske statsborgere) eller opholdstilladelse (hvis ikke dansk) for at vurdere din ansøgning. CV og ansøgningstekst er valgfrit.</p>
            </div>
            <FileField
              label="Kørekort (forside)"
              required
              hint="Skarp billede eller scan af kørekortets forside"
              value={files.drivers_license_front}
              onChange={setFile("drivers_license_front")}
              testid="driver-doc-license-front"
            />
            <FileField
              label="Kørekort (bagside)"
              required
              hint="Skarp billede eller scan af kørekortets bagside"
              value={files.drivers_license_back}
              onChange={setFile("drivers_license_back")}
              testid="driver-doc-license-back"
            />
            <FileField
              label="Straffeattest"
              required
              hint="Privat straffeattest (PDF fra Politi.dk)"
              value={files.criminal_record_doc}
              onChange={setFile("criminal_record_doc")}
              testid="driver-doc-criminal"
            />
            <FileField
              label={form.is_danish_citizen ? "Pas" : "Pas (hvis du har dansk pas)"}
              required={form.is_danish_citizen}
              hint="Foto af pas-billedsiden eller scan af pas"
              value={files.passport_doc}
              onChange={setFile("passport_doc")}
              testid="driver-doc-passport"
            />
            <FileField
              label={form.is_danish_citizen ? "Opholdstilladelse / Arbejdstilladelse (hvis du har en)" : "Opholdstilladelse / Arbejdstilladelse"}
              required={!form.is_danish_citizen}
              hint="Gyldig opholdstilladelse eller arbejdstilladelse fra SIRI"
              value={files.work_permit_doc}
              onChange={setFile("work_permit_doc")}
              testid="driver-doc-work-permit"
            />
            <div className="sm:col-span-2">
              <FileField
                label="CV / ansøgning (valgfrit)"
                hint="PDF, JPG eller PNG · max 10 MB"
                value={files.cv_doc}
                onChange={setFile("cv_doc")}
                testid="driver-doc-cv"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="label-flat">Erfaring (valgfrit)</label>
              <textarea rows={3} className="input-flat" value={form.experience} onChange={update("experience")} data-testid="driver-experience" placeholder="Fx tidligere job som chauffør, bud eller logistik" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-flat">Hvorfor EMA Kurer? (valgfrit)</label>
              <textarea rows={3} className="input-flat" value={form.motivation} onChange={update("motivation")} data-testid="driver-motivation" placeholder="Skriv kort hvorfor du gerne vil køre for os — eller vedhæft det som CV ovenfor" />
            </div>

            {/* GDPR */}
            <div className="sm:col-span-2 bg-slate-50 border border-slate-200 p-5 mt-2" data-testid="driver-gdpr">
              <div className="flex items-start gap-3">
                <Shield size={20} className="text-brand-red flex-shrink-0 mt-0.5" />
                <div className="text-sm text-brand-text2 leading-relaxed">
                  <strong className="text-brand-dark">Persondata & GDPR.</strong> Når du sender din ansøgning, behandler EMA Kurer ApS (CVR 46465717) dine personoplysninger og uploadede dokumenter (kørekort, straffeattest, CV) udelukkende med det formål at vurdere din ansøgning. Vi opbevarer dine data så længe det er nødvendigt og senest i 6 måneder efter ansøgningens afslutning, hvorefter de slettes permanent. Du har til enhver tid ret til indsigt, berigtigelse, sletning og at trække dit samtykke tilbage ved at kontakte <a href="mailto:kontakt@emakurer.dk" className="text-brand-red underline">kontakt@emakurer.dk</a>. Læs mere i vores <Link to="/handelsbetingelser#privacy" className="text-brand-red underline">privatlivspolitik</Link>.
                </div>
              </div>
              <label className="flex items-start gap-2 mt-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={gdpr}
                  onChange={() => setGdpr((v) => !v)}
                  data-testid="driver-gdpr-consent"
                  className="mt-1"
                />
                <span className="text-sm text-brand-dark">
                  Jeg giver hermed samtykke til, at EMA Kurer ApS behandler mine personoplysninger og vedhæftede dokumenter med henblik på rekruttering.
                </span>
              </label>
            </div>

            <div className="sm:col-span-2">
              <button type="submit" className="btn-primary" disabled={busy} data-testid="driver-submit">
                {busy ? <><Loader2 className="animate-spin" size={16} /> Sender...</> : <>Send ansøgning <ArrowRight size={16} /></>}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
